import { DatePicker, Form, Input, InputNumber, Select } from "antd";
import { useEffect, useState } from "react";
import { TerritorySelect } from "@/modules/territories/components/TerritorySelect";
import { useTerritories } from "@/modules/territories/hooks/useTerritories";
import { usePlantContracts, usePlants } from "../hooks/usePlantings";
import type { PlantingGeometry } from "../types";
import { GeometryPicker } from "./GeometryPicker";

type DrawMode = "point" | "polygon";

interface PlantingFormProps {
	organizationId?: string;
}

const GEOMETRY_TYPES: PlantingGeometry["type"][] = [
	"Point",
	"MultiPoint",
	"Polygon",
	"MultiPolygon",
];

const POINT_TYPES: PlantingGeometry["type"][] = ["Point", "MultiPoint"];

function isValidGeometry(value: unknown): value is PlantingGeometry {
	if (!value || typeof value !== "object") {
		return false;
	}
	const geom = value as { type?: unknown; coordinates?: unknown };
	if (typeof geom.type !== "string" || !GEOMETRY_TYPES.includes(geom.type as never)) {
		return false;
	}
	return Array.isArray(geom.coordinates);
}

export function PlantingForm({ organizationId }: PlantingFormProps) {
	const [drawMode, setDrawMode] = useState<DrawMode>("point");
	const form = Form.useFormInstance();
	const plantId = Form.useWatch("plantId", form) as string | undefined;
	const territoryId = Form.useWatch("territoryId", form) as string | undefined;

	const { data: plantsData } = usePlants(organizationId ?? "");
	const { data: contractsData } = usePlantContracts(organizationId ?? "");
	const { data: territoriesData } = useTerritories({
		organizationId: organizationId ?? "",
		page: 1,
		limit: 1000,
		flat: true,
	});

	const plants = plantsData?.data?.items ?? [];
	const contracts = contractsData?.data?.items ?? [];
	const territories = territoriesData?.data?.items ?? [];
	const selectedTerritory = territories.find((t) => t.id === territoryId);

	// Backend rule: DARAXT -> Point/MultiPoint, KO'CHAT/GUL -> Polygon/MultiPolygon
	const selectedPlant = plants.find((p) => p.id === plantId);
	const requiredMode: DrawMode | undefined = selectedPlant
		? selectedPlant.type === "DARAXT"
			? "point"
			: "polygon"
		: undefined;

	useEffect(() => {
		if (requiredMode) {
			setDrawMode(requiredMode);
		}
	}, [requiredMode]);

	const validateGeometry = (value: unknown) => {
		if (!isValidGeometry(value)) {
			return Promise.reject(new Error("Noto'g'ri GeoJSON geometriyasi"));
		}
		const isPoint = POINT_TYPES.includes(value.type);
		if (requiredMode === "point" && !isPoint) {
			return Promise.reject(new Error("Daraxt uchun nuqta (Point) belgilanishi kerak"));
		}
		if (requiredMode === "polygon" && isPoint) {
			return Promise.reject(new Error("Ko'chat va gul uchun poligon chizilishi kerak"));
		}
		return Promise.resolve();
	};

	return (
		<>
			<Form.Item name="organizationId" hidden>
				<Input />
			</Form.Item>
			<div className="grid grid-cols-2 gap-4">
				<Form.Item
					name="plantId"
					label="O'simlik"
					rules={[{ required: true, message: "O'simlikni tanlang" }]}
				>
					<Select
						placeholder="O'simlikni tanlang"
						showSearch
						filterOption={(input, option) =>
							(option?.label as string)?.toLowerCase().includes(input.toLowerCase())
						}
						options={plants.map((p) => ({ value: p.id, label: p.name }))}
					/>
				</Form.Item>
				<Form.Item name="contractId" label="Shartnoma">
					<Select
						placeholder="Shartnomani tanlang (ixtiyoriy)"
						allowClear
						showSearch
						filterOption={(input, option) =>
							(option?.label as string)?.toLowerCase().includes(input.toLowerCase())
						}
						options={contracts.map((c) => ({
							value: c.id,
							label: c.contractNumber ?? c.id,
						}))}
					/>
				</Form.Item>
				<Form.Item
					name="territoryId"
					label="Hudud"
					rules={[{ required: true, message: "Hududni tanlang" }]}
				>
					<TerritorySelect
						organizationId={organizationId}
						placeholder="Hududni tanlang"
						allowClear
					/>
				</Form.Item>
				<Form.Item
					name="plantedAt"
					label="Ekilgan sana"
					rules={[{ required: true, message: "Ekilgan sanani tanlang" }]}
				>
					<DatePicker showTime style={{ width: "100%" }} format="DD.MM.YYYY HH:mm" />
				</Form.Item>
				<Form.Item name="quantity" label="Soni">
					<InputNumber min={0} style={{ width: "100%" }} placeholder="Masalan: 100" />
				</Form.Item>
			</div>
			<Form.Item
				name="geometry"
				label="Geometriya"
				extra={
					requiredMode
						? requiredMode === "point"
							? "Daraxt uchun nuqta (Point) belgilanadi"
							: "Ko'chat va gul uchun poligon (Polygon) chiziladi"
						: undefined
				}
				rules={[{ validator: (_rule, value) => validateGeometry(value) }]}
			>
				<GeometryPicker
					mode={requiredMode ?? drawMode}
					onModeChange={setDrawMode}
					modeDisabled={!!requiredMode}
					boundary={selectedTerritory?.coordinates}
				/>
			</Form.Item>
		</>
	);
}
